// Temporarily importing WellLogsWidget as WellLogsManager until the actual component is available
import { WellLogsManagerWidget as WellLogsManagerWidgetComponent } from '@go-widgets/well-logs-widget';
import { FullArrowLight as FullIcon, ReduceLight as MinimizeIcon, Refresh2Light } from '@profgeosoft-ui/icons';
import { Button, Tooltip } from '@profgeosoft-ui/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useService } from 'src/packages/di';

import type { WellLogsManagerWidgetEntity } from './WellLogsManagerWidget.entity';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';

import { WellLogsManagerWidgetStore } from './WellLogsManagerWidget.store';

import styles from './WellLogsManagerWidget.module.scss';

type WrapperProps = {
  entity: WellLogsManagerWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: WellLogsManagerWidgetStore;
  entity: WellLogsManagerWidgetEntity;
  options: TWidgetRendererOptions;
};

export const WellLogsManagerWidgetWrapper = observer(function WellLogsManagerWidgetWrapper({
  entity,
  options,
}: WrapperProps) {
  const [store, setStore] = useState<WellLogsManagerWidgetStore | null>(null);

  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const _store = storeService.getStore<WellLogsManagerWidgetEntity, WellLogsManagerWidgetStore>(entity);

    if (_store) {
      setStore(_store);
    } else {
      const newStore = new WellLogsManagerWidgetStore(entity, options.groupSelectStore, options.wellListEntity);

      storeService.setStore<WellLogsManagerWidgetEntity, WellLogsManagerWidgetStore>(entity, newStore);

      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, options.wellListEntity]);

  if (!store) return null;

  return <WellLogsManagerWidget store={store} entity={entity} options={options} />;
});

export const WellLogsManagerWidget = observer(function WellLogsManagerWidget({ entity, options, store }: Props) {
  const { setGroupForWellLogsManager, exitFullScreen, isReady, effect, progress, handleReloadWellStructure } = store;

  const { t } = useTranslation();

  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);

  const { wellId, isFullscreen, state, setFullscreen, wellboreName } = entity;

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.wellLogsManagerWidgetWrapper} ref={(ref) => setWrapperElement(ref)}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWellLogsManager}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        progress={progress}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          if (!isReady) return null;

          return (
            <>
              <Tooltip title={t('common:Widget.reloadStructure')} delay={400}>
                <Button
                  onClick={handleReloadWellStructure}
                  variant="flat"
                  icon={<Refresh2Light className={styles.icon} />}
                  className={styles.button}
                />
              </Tooltip>
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => exitFullScreen()}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />

      {!wellId && <NoWellChosen />}

      {wrapperElement &&
        (state ? (
          <div className={styles.wrapper}>
            {state && <WellLogsManagerWidgetComponent widgetState={state} externalContainer={wrapperElement} />}
          </div>
        ) : (
          <NoWellChosen />
        ))}
    </div>
  );
});
